<template>
	<button
		v-if="matched"
		type="button"
		class="flex cursor-pointer items-center gap-1.5 rounded bg-white p-2 text-left shadow-small-card disabled:cursor-not-allowed"
		:disabled="!anchor"
		@mouseenter="anchor && emit('hover', anchor)"
		@mouseleave="emit('leave')"
		@click="anchor && emit('scroll-to', anchor)"
	>
		<span class="text-won-green">✓</span>
		<span class="text-[12px] text-won-black">{{ message }}</span>
	</button>

	<div
		v-else
		:class="[
			'w-full rounded-md border p-2 transition-colors',
			resolved ? 'border-won-green bg-won-green-light' : 'border-won-gray-light bg-white',
		]"
		@mouseenter="!resolved && anchor && emit('hover', anchor)"
		@mouseleave="emit('leave')"
	>
		<button
			type="button"
			class="flex w-full cursor-pointer items-start gap-1 text-left disabled:cursor-default"
			:disabled="!anchor || resolved"
			@click="anchor && emit('scroll-to', anchor)"
		>
			<CheckCircleIcon
				v-if="resolved"
				class="mt-0.5 size-4 shrink-0 text-won-green"
			/>

			<span
				v-else
				:class="['mt-1 size-1.5 shrink-0 rounded-full', dotColor]"
			/>

			<span class="flex min-w-0 flex-1 flex-col">
				<span
					v-if="label"
					class="text-[12px] font-medium text-won-secondary uppercase"
				>{{ label }}</span>
				<span class="text-[14px] text-won-black">{{ message }}</span>
			</span>
		</button>

		<!-- EN-1146: a Content Strength card's Rewrite, then Accept and Undo until the user settles it. -->
		<div
			v-if="!resolved && (canRewrite || rewriteStatus)"
			class="mt-2 flex justify-end gap-2"
		>
			<template v-if="rewriteStatus === 'review' || rewriteStatus === 'undoing'">
				<button
					type="button"
					class="
						cursor-pointer rounded border border-won-gray-light px-3 py-1 text-[12px] font-medium
						text-won-black disabled:cursor-not-allowed disabled:opacity-50"
					:disabled="rewriteStatus === 'undoing'"
					@click="emit('undo')"
				>
					Undo
				</button>

				<button
					type="button"
					class="
						cursor-pointer rounded bg-won-green px-3 py-1 text-[12px] font-medium text-white
						disabled:cursor-not-allowed disabled:opacity-50"
					:disabled="rewriteStatus === 'undoing'"
					@click="emit('accept')"
				>
					Accept
				</button>
			</template>

			<button
				v-else
				type="button"
				class="
					flex cursor-pointer items-center gap-1 rounded border border-won-green px-3 py-1 text-[12px]
					font-medium text-won-green disabled:cursor-not-allowed disabled:opacity-50"
				:disabled="rewriteStatus === 'rewriting'"
				@click="emit('rewrite')"
			>
				<SparklesIcon :class="['size-4', { 'animate-pulse': rewriteStatus === 'rewriting' }]" />
				{{ rewriteStatus === 'rewriting' ? 'Rewriting…' : 'Rewrite' }}
			</button>
		</div>
	</div>
</template>

<script setup>
import { CheckCircleIcon } from '@heroicons/vue/24/outline';
import { SparklesIcon } from '@heroicons/vue/16/solid';

defineProps({
	// Small uppercase label above the message, e.g. "Missing Skill" or the
	// warning's category ("ADD DETAIL"). Omitted for matched-skill rows.
	label: {
		type: String,
		default: null,
	},
	message: {
		type: String,
		required: true,
	},
	// DOM id (or synthetic id) the hover/click events resolve to a highlight
	// target with — see sidebar-scorer.vue's warningHoverHandler/scrollTargetIntoView.
	anchor: {
		type: String,
		default: null,
	},
	dotColor: {
		type: String,
		default: 'bg-won-yellow',
	},
	// Matched-skill rows render as a compact confirmation row (✓ + name)
	// instead of the bordered missing-skill/warning card.
	matched: {
		type: Boolean,
		default: false,
	},
	// EN-812: the user just fixed it. The card shows a brief success state, then the panel
	// removes it (useResolvingSuggestions); meanwhile it no longer points at the resume.
	resolved: {
		type: Boolean,
		default: false,
	},
	// EN-1146: a Content Strength card the Rewrite action can fix right now (scorer.store's withRewrites).
	canRewrite: {
		type: Boolean,
		default: false,
	},
	// EN-1146: where the card's rewrite is: 'rewriting', then 'review' (Accept / Undo), 'undoing',
	// 'accepting' (waiting for a score of the rewrite) or 'accepted'. Null when there is none.
	rewriteStatus: {
		type: String,
		default: null,
	},
});

const emit = defineEmits(['hover', 'leave', 'scroll-to', 'rewrite', 'accept', 'undo']);
</script>
